import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTable, { Column, DerivedDataObject, ExpandedRows, Instance, Resize } from 'react-table-6';
import { getVoiceMailAudioUrl, playVoiceMail } from '../api';
import { HasClassName } from '../component-utils';
import { formatDuration } from '../date-utils';
import { useSelector } from '../hook-utils';
import { teamsOpenForwardReplayDialog } from '../legacy';
import { EventSubscription, ExtensionStatus, Identifier, VoiceMail, VoiceMailFolder, VoiceMailStatus } from '../model';
import { actions } from '../store';
import { selectMyAccess, selectMyData, selectMyself } from '../store/reducers/config';
import { ColumnWidths, getGridSortOrder, selectAchieve, selectGridColumnWidths, selectInIpfxMobility } from '../store/reducers/localSettings';
import { selectExtension } from '../store/reducers/queueAgents';
import { getAudioPlaying } from '../store/reducers/settings';
import { selectUnreadVmCount, selectVoiceMailFolder, shouldFetchVoiceMail } from '../store/reducers/voiceMail';
import { TableRow } from '../table-utils';
import { RelativeShortFormatDateTime } from './CallsPage';
import { ReactComponent as SearchIcon } from './icons/toolbar-search.svg';
import { ReactComponent as IconPrivate } from './icons/voicemail/private.svg';
import { ReactComponent as IconUrgent } from './icons/voicemail/urgent.svg';
import { ReactComponent as IconUnReadStatus } from './icons/voicemail/voicemail-normal.svg';
import { ReactComponent as IconReadStatus } from './icons/voicemail/voicemail-open.svg';
import { ReactComponent as IconPlay } from './icons/play.svg';
import { ReactComponent as IconVoicemailPhonePlay } from './icons/voicemail-phone_play.svg'
import { ReactComponent as IconDial } from './icons/dial.svg'
import { ReactComponent as IconForwardVoicemail } from './icons/voicemail/forward_voicemail.svg'
import { ReactComponent as IconVoicemailNew } from './icons/voicemail/voicemail_new.svg'
import { ReactComponent as IconSave } from './icons/save.svg'
import { ReactComponent as IconDelete } from './icons/delete.svg'
import Input from './Input';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from './Menu';
import MoreButton from './MoreButton';
import SidebarView from './SidebarView';
import './VoiceMailPage.scss';

function getColumns(onPlay: (vm: VoiceMail, index: number) => void, onMore: (state: VoicemailMenuState) => void, folder: VoiceMailFolder, columnsWidths?: ColumnWidths): Array<Column<VoiceMail>> {
  const columns: Array<Column<VoiceMail>> = [{
    id: 'expander',
    expander: true,
    width: 0,
  }, {
    id: 'icon-priority',
    Header: '',
    width: 34,
    accessor: r => r.urgent,
    Cell: ({ value }) => value && <div className='grid-icon' title='Urgent voicemail'><IconUrgent /></div>,
  }, {
    id: 'icon',
    Header: '',
    width: 34,
    accessor: r => r.status,
    Cell: ({ original: r }) => <VoicemailIcon item={r} />,
  }, {
    id: 'icon-private',
    Header: '',
    width: 34,
    accessor: r => r.personal,
    Cell: ({ value }) => value && <div className='grid-icon' title='Private voicemail'><IconPrivate /></div>,
  }, {
    id: 'from',
    Header: folder === 'Sent' ? 'To' : 'From',
    accessor: i => i.caller.name ? `${i.caller.name} (${i.caller.id})` : i.caller.id,
  }, {
    id: 'subject',
    Header: 'Subject',
    accessor: i => i.subject,
  }, {
    id: 'date',
    Header: 'Received',
    accessor: vm => vm.received,
    Cell: ({ original: r }) => <RelativeShortFormatDateTime date={r.received} />,
    className: 'numeric',
    headerClassName: 'numeric',
    width: 130,
  }, {
    id: 'duration',
    Header: 'Duration',
    accessor: i => i.duration,
    Cell: cell => formatDuration(cell.value / 1000),
    className: 'numeric',
    headerClassName: 'numeric',
    width: 100,
  }, {
    id: 'menuButton',
    Cell: ({ original: vm, viewIndex }: TableRow<VoiceMail>) => <div className='actions'>
      <button onClick={() => onPlay(vm, viewIndex)} title='Play' className='play'>
        <svg viewBox='0 0 24 24' strokeWidth='1' strokeLinecap='round' strokeLinejoin='round'>
          <polygon points='5 3 19 12 5 21 5 3' />
        </svg>
      </button>
      <MoreButton className='menu-button' onClick={e => {
        e.preventDefault()
        onMore({ event: e, target: targetFromEvent(e), item: vm, index: viewIndex })
      }
      } />
    </div>,
    width: 70,
    sortable: false,
    resizable: false,
  }]
  return columns.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

export interface VoicemailMenuState {
  event: React.MouseEvent,
  target: MenuTarget,
  item: VoiceMail,
  index: number
}

export const FolderNames: VoiceMailFolder[] = ['Inbox', 'Sent', 'Saved', 'Future', 'Deleted']

export interface VoiceMailPageProps extends HasClassName {
  extension?: Identifier
}

export interface VoicemailIconProps extends HasClassName {
  item: VoiceMail
}

export default function VoiceMailPage({ extension, className }: VoiceMailPageProps) {
  console.log('Render VoiceMailPage')

  const dispatch = useDispatch()

  const [folder, setFolder] = useState(FolderNames[0])
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [expanded, setExpanded] = useState<ExpandedRows>({});
  const [menu, setMenu] = useState<VoicemailMenuState>()
  const [lastSelected, setLastSelected] = useState<string | undefined>(undefined)
  const [selected, setSelected] = useState<string[]>([])
  const [playing, setPlaying] = useState<string>('')
  const [filter, setFilter] = useState('')
  const [filterKeyPress, setFilterKeyPress] = useState('')
  const isAchieve = useSelector(selectAchieve)
  const meDetails = useSelector(selectMyData)

  const me = useSelector(selectMyself)
  const ext = extension || me
  const unread = useSelector(s => ext && selectUnreadVmCount(s, ext)) || 0
  const messages = useSelector(state => ext && selectVoiceMailFolder(state, ext, folder))
  const shouldFetch = useSelector(state => ext && shouldFetchVoiceMail(state, ext, folder))
  const [data, setData] = useState<VoiceMail[]>([])
  const gridType = 'voicemail'
  const sorted = useSelector(s => getGridSortOrder(s, gridType))
  const widths = useSelector(s => selectGridColumnWidths(s, gridType))
  const refColumnResizeState = useRef<ColumnWidths>()

  let reactTable = useRef<ReactTable<VoiceMail>>(null);

  function onKeyDown(e: KeyboardEvent) {
    if (data == null || data.length === 0 || reactTable.current === null) return

    const table = reactTable.current as Instance
    const sorted: VoiceMail[] = (table.getSortedData(table.getResolvedState()).sortedData as DerivedDataObject[]).map(x => x._original)

    function selectFirstRow() {
      if (selected.length === 0 && sorted.length > -1) {
        const first = sorted[0]
        setSelected([first.id])
        setLastSelected(first.id)
        dispatch(actions.setSelectedNumber(first.caller.id))
        return true
      }
      return false
    }

    function selectNextUp() {
      const index = sorted.findIndex(vm => lastSelected === vm.id)
      return index - 1
    }

    function selectNextDown() {
      const index = sorted.findIndex(vm => lastSelected === vm.id)
      return index + 1
    }

    switch (e.keyCode) {
      case 38://up
        e.preventDefault()

        if (selectFirstRow()) return

        const prevIndex = selectNextUp()
        if (prevIndex < 0) return
        const prev = sorted[prevIndex].id

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {

          if (selected.find(s => s === lastSelected) && selected.find(s => s === prev)) {
            setSelected(selected.filter(s => s !== lastSelected))
            setLastSelected(prev)
          } else {
            setSelected([...selected, prev])
            setLastSelected(prev)
          }

          dispatch(actions.clearSelectedNumber())

        } else {

          setSelected([prev])
          setLastSelected(prev)
          dispatch(actions.setSelectedNumber(sorted[prevIndex].caller.id))
        }
        break
      case 40://down
        e.preventDefault()

        if (selectFirstRow()) return

        const nextIndex = selectNextDown()
        if (nextIndex >= sorted.length) return
        const next = sorted[nextIndex].id

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {

          if (selected.find(s => s === lastSelected) && selected.find(s => s === next)) {
            setSelected(selected.filter(s => s !== lastSelected))
            setLastSelected(next)
          } else {
            setSelected([...selected, next])
            setLastSelected(next)
          }

          dispatch(actions.clearSelectedNumber())
        } else {

          setSelected([next])
          setLastSelected(next)
          dispatch(actions.setSelectedNumber(sorted[nextIndex].caller.id))
        }

        break
    }
  }

  const onKeyDownRef = useRef(onKeyDown)

  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  })

  const onPlay = useCallback(async (vm: VoiceMail, index: number) => {
    if (ext === null) { return }

    if (urls[vm.id]) {
      setUrls({})
      setPlaying('')
      dispatch(actions.requestAudioPlaying('', -1))
    } else {
      const url = await getVoiceMailAudioUrl(ext, vm.id, vm.folder)
      setUrls({ [vm.id]: url })
      setPlaying(vm.id)
      dispatch(actions.requestAudioPlaying(url, 0))
    }

  }, [dispatch, ext, urls])

  useEffect(() => {
    if (ext) {
      dispatch(actions.fetchVoiceMail.request({ mailbox: ext, folder, subscription: EventSubscription.Add }))
    }
  }, [dispatch, folder, ext, shouldFetch])

  useEffect(() => {
    return () => {
      setSelected([])
      setLastSelected(undefined)
      dispatch(actions.clearSelectedNumber())
    }
  }, [dispatch])

  const f = filterKeyPress.toLowerCase()

  useEffect(() => {
    setData(f !== '' && messages ? messages.filter(c => {
      return c.caller.name.toLowerCase().indexOf(f) > -1 ||
        c.caller.id.toLowerCase().indexOf(f) > -1 ||
        c.subject.toLowerCase().indexOf(f) > -1
    }) : messages !== null ? messages : [])
  }, [f, messages])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      onKeyDownRef.current(e)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const onColumnResize = useCallback((newResized: Resize[], event: any) => {
    newResized.forEach(element => {
      if (refColumnResizeState.current) {
        refColumnResizeState.current = { ...refColumnResizeState.current, [element.id]: element.value }
      } else {
        refColumnResizeState.current = { [element.id]: element.value }
      }
    });
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  const table = useMemo(() => messages && <ReactTable ref={reactTable}
    data={data ? data : []}
    columns={getColumns(onPlay, (state) => {
      setMenu(state)
    }, folder, widths)
    } showPagination={false} defaultPageSize={9999999}
    minRows={0} NoDataComponent={() => null}
    getTrProps={(_: any, row?: TableRow<VoiceMail>) => row && ({
      className: (row && row.original.status === VoiceMailStatus.Unread ? 'unread ' : ' ') + ((menu && menu.item.id === row.original.id) || (selected.length > 0 && selected.findIndex(f => f === row.original.id) !== -1) ? ' selected' : ' '),
      onClick(e: React.MouseEvent) {
        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          setSelected([...selected, row.original.id])
          setLastSelected(row.original.id)

          if (selected.length === 0) {
            dispatch(actions.setSelectedNumber(row.original.caller.id))
          } else {
            dispatch(actions.clearSelectedNumber())
          }

        } else {
          setSelected([row.original.id])
          setLastSelected(row.original.id)
          dispatch(actions.setSelectedNumber(row.original.caller.id))
        }
      },
      onContextMenu(e: React.MouseEvent) {
        setMenu({ event: e, target: targetFromEvent(e), item: row.original, index: -1 })
      },
    })}
    expanded={expanded}
    sorted={sorted}
    onSortedChange={(newSort, column) => {
      setExpanded({})
      setUrls({})
      dispatch(actions.requestSetGridSortOrder(gridType, [newSort[0]]))
    }}
    onResizedChange={onColumnResize}
    ExpanderComponent={() => null}
    SubComponent={(row: TableRow<VoiceMail>) =>
      urls[row.original.id] !== undefined && <Audio play={playing === row.original.id} file={urls[row.original.id]} />
    } />
    , [messages, data, onPlay, folder, widths, expanded, sorted, onColumnResize, menu, selected, dispatch, urls, playing])

  useEffect(() => {
    //hack to manage expanded index when the messages change i.e. another one is added
    if (reactTable.current && urls) {
      const table = reactTable.current as Instance
      const sorted: VoiceMail[] = (table.getSortedData(table.getResolvedState()).sortedData as DerivedDataObject[]).map(x => x._original)
      for (let index = 0; index < sorted.length; index++) {
        const element = sorted[index];
        if (urls[element.id] && !expanded[index]) {
          setExpanded({ [index]: true })
        }
      }
    }
    //console.info("messages mount")
    return () => {
      //console.info("messages unmount")
    }
  }, [expanded, messages, table, urls])

  return <SidebarView className={className} value={folder} onChange={f => {
    setFolder(f)
    setUrls({})
    setExpanded({})
  }}>
    <SidebarView.Sidebar>
      <SidebarView.Header>{isAchieve ? meDetails?.fullName : 'Voicemail'}</SidebarView.Header>
      {FolderNames.map(f => <SidebarView.Item key={f} value={f}>
        <FolderName folder={f} unread={unread} />
      </SidebarView.Item>)}
    </SidebarView.Sidebar>
    <SidebarView.Body>
      <SidebarView.Header>
        <div>{getFolderName(folder, 0)}</div>
        <div className='searchBox'>
          <Input placeholder={getFolderName(folder, 0) === 'Sent' ? 'To / subject' : 'From / subject'} className='search' value={filter} onChange={e => setFilter(e.currentTarget.value)} autocomplete={false}
            onKeyUp={e => {
              if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
                e.stopPropagation()
                e.preventDefault()

                setFilterKeyPress(filter)

                setSelected([])
                setLastSelected(undefined)
                dispatch(actions.clearSelectedNumber())

              } else if (e.nativeEvent.code === 'Escape') {
                setFilter('')
                setFilterKeyPress('')
              }

            }} />
          <SearchIcon className='searchIcon' />
        </div>
      </SidebarView.Header>
      <div className='VoiceMailPage'>{table}</div>
      {menu && ext && <VoicemailContextMenu state={menu} selected={selected} mailbox={ext} onClose={() => setMenu(undefined)} />}
    </SidebarView.Body>
  </SidebarView>
}

export function Audio({ play, file }: { play: boolean, file: string }) {
  const dispatch = useDispatch()
  const playing = useSelector(getAudioPlaying)
  const ref = useRef<HTMLAudioElement>(null)
  // play ended
  //playing.current === -1

  useEffect(() => {
    const p = playing
    const r = ref
    if (playing.file === file && playing.current > -1 && ref.current !== null) {
      ref.current.currentTime = playing.current
    }
    return () => {
      if (r.current !== null) {
        if (p.current === -1 || p.current === r.current.currentTime) return

        dispatch(actions.requestAudioPlaying(file, r.current.currentTime))
      }
    }
  }, [dispatch, file, playing])

  return <audio ref={ref} autoPlay={play && playing.current > -1} controls src={file} onEnded={() => dispatch(actions.requestAudioPlaying(file, -1))} />
}

export function VoicemailIcon({ item }: VoicemailIconProps) {
  const icon = getVoicemailIcon(item)
  return icon && React.cloneElement(icon, { className: 'grid-icon' })
}

function getVoicemailIcon(item: VoiceMail) {
  if (item.status === VoiceMailStatus.Read) {
    return <div title='Voicemail read'><IconReadStatus /></div>
  } else if (item.status === VoiceMailStatus.Unread) {
    return <div title='Voicemail unread'><IconUnReadStatus /></div>
  } else {
    return null
  }
}

export function VoicemailContextMenu({ state: { item: vm, target, index }, onClose, mailbox, selected, onPlay }: { state: VoicemailMenuState, onClose(): void, mailbox: Identifier, selected: string[], onPlay?: (vm: VoiceMail, index: number) => void }) {
  const dispatch = useDispatch()
  const ipfxMobilityClient = useSelector(selectInIpfxMobility)
  const access = useSelector(selectMyAccess)
  const myId = useSelector(selectMyself);
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)
  const playing = false // playing of the selected
  const items = Array.from(new Set([...selected, vm.id]))  //unquie ids


  if (!myId || !myself) { return null }

  const dialEnabled = (myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released)

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    {onPlay && <MenuItem onClick={() => onPlay(vm, index)} >
      <IconPlay />
      Play
    </MenuItem>}
    {access.features.phoneDial && <MenuItem onClick={() => { if (dialEnabled) playVoiceMail(mailbox, vm.id, vm.folder) }} disabled={!dialEnabled || items.length > 1} >
      <IconVoicemailPhonePlay />
      Play via phone
    </MenuItem>}
    {access.features.phoneDial && <MenuItem onClick={() => { if (dialEnabled) dispatch(actions.ccDial(vm.caller.id)) }} disabled={!dialEnabled || vm.caller.id === myId.id || items.length > 1} >
      <IconDial />
      Dial
    </MenuItem>}

    {!ipfxMobilityClient && <MenuDivider />}
    {!ipfxMobilityClient && <MenuItem onClick={() => teamsOpenForwardReplayDialog(vm.id, 'SEND_REPLY', vm.caller.id, vm.subject, mailbox.id, mailbox.server)} disabled={vm.caller.id === myId.id || playing || items.length > 1}>

      Reply...
    </MenuItem>}
    {!ipfxMobilityClient && <MenuItem onClick={() => teamsOpenForwardReplayDialog(vm.id, 'SEND_FORWARD', vm.caller.id, vm.subject, mailbox.id, mailbox.server)} disabled={vm.personal || playing || items.length > 1}>
      <IconForwardVoicemail />
      Forward...
    </MenuItem>}
    {!ipfxMobilityClient && <MenuItem onClick={() => teamsOpenForwardReplayDialog('', 'SEND_NEW', myId.id, '', mailbox.id, mailbox.server)}>
      <IconVoicemailNew />
      New
    </MenuItem>}

    {(access.features.phoneDial || access.features.phoneDial) && <MenuDivider />}
    {vm.folder !== 'Saved' && <MenuItem onClick={() => dispatch(actions.requestSaveVoiceMails(mailbox, items, vm.folder))}>
      <IconSave />
      Save
    </MenuItem>}
    <MenuItem onClick={() => {
      dispatch(actions.requestDeleteVoiceMails(mailbox, items, vm.folder))
    }}>
      <IconDelete />
      Delete
    </MenuItem>
  </Menu>
}

export function getFolderName(f: VoiceMailFolder, unread: number) {
  return f[0].toUpperCase() + f.substr(1) + (f === "Inbox" ? unread > 0 ? ' (' + unread + ')' : '' : '')
}

function FolderName({ folder, unread }: { folder: VoiceMailFolder, unread: number }) {
  return <span className={folder === 'Inbox' && unread > 0 ? 'bold' : ''}>{getFolderName(folder, unread)}</span>
}
